import * as React from "react";
import { Alert, Button, ButtonGroup } from "react-bootstrap";
import BootstrapTable from "react-bootstrap-table-next";
import cellEditFactory from "react-bootstrap-table2-editor";
import { isUndefined } from "lodash";

import { IControlError, IControlSchema } from "../../../core/Controls";

import {
	Component,
	I18NString,
	numberCommaToDot,
	numberDotToComma,
	parseFloatCommaDot,
	strCommaToDot,
} from "../../helpers/Utils";
import { IDispatcher } from "../../helpers/Dispatcher";

import ErrorAlert from "../components/ErrorAlert";

import Field from "../components/Field";

import InputNumber from "./components/InputNumber";

import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";

interface FixedTableColumn {
	options?: any;
	type?: any;
	title: I18NString;
	name: string;
	isKey: boolean;
	order: number;
	dataField: string;
	placeholder?: I18NString;
}

interface FixedTableCell {
	options: any;
	disabled?: boolean;
	name: string;
	type: string;
	value: I18NString;
}

interface FixedTableRow {
	order: number;
	parent?: any;
	internal_id?: any;
	data: Array<FixedTableCell>;
	type?: string;
}

interface FixedTableSchema extends IControlSchema {
	columns: Array<FixedTableColumn>;
	rows: Array<FixedTableRow>;
	disabled?: boolean;
	value?: Array<any>;
	description?: I18NString;
}

interface IFixedTableProps {
	schema: FixedTableSchema;
	api: any;
	dispatcher?: IDispatcher;
	error: IControlError | undefined;
	visited: boolean;
	allSections: unknown;
}

interface IFixedTableState {
	values: Array<any>;
	showHelp: boolean;
	empty: boolean;
}

const emptyChecker = (value, row, rowIndex, formatExtraData) => {
	// console.log(row);
	// console.log(rowIndex);
	// console.log(formatExtraData);

	if (formatExtraData.columnType === "select") {
		const options = row[`${formatExtraData.dataField}_options`];

		if (options) {
			return options?.find((item) => item.value === +value)?.label ?? "-";
		}
		return formatExtraData.defaultOptionsIfHave?.find((item) => item.value === +value)?.label ?? "-";
	}

	// eslint-disable-next-line no-nested-ternary
	return !value
		? formatExtraData.columnType !== "label" &&
		  row[`${formatExtraData.dataField}_type`] !== "label" &&
		  row[`${formatExtraData.dataField}_type`] !== "children_sum"
			? formatExtraData.placeholder
			: ""
		: value;
};

const sortArrayByProperty = <T, K extends keyof T>(items: Array<T>, byProperty: K): Array<T> =>
	[...items].sort((a: T, b: T) => {
		if (a[byProperty] < b[byProperty]) return -1;
		if (a[byProperty] > b[byProperty]) return 1;
		return 0;
	}) || [];

class FixedTable extends Component<IFixedTableProps, IFixedTableState> {
	state = {
		values: [],
		showHelp: false,
		empty: true,
	};

	componentDidMount() {
		const {
			schema: { columns, value },
		} = this.props;

		if (!value) {
			this.setState({
				values: this.getRowsSorted().map((row: FixedTableRow) => {
					const res: Record<string, unknown> = {};
					for (let i = 0; i < row.data.length; i++) {
						const item = row.data[i];
						res[item.name] = item?.value || null;
						res[`${item.name}_type`] = this.getDominatingType(columns[i].type, item.type);
						if (item.type === "select" && item.options) {
							res[`${item.name}_options`] = item.options;
						}
					}
					res.internal_id = row.internal_id;
					res.parent = row.parent;
					res.type = row.type;

					return res;
				}),
				empty: true,
			});
		} else {
			this.setState({
				values: typeof value === "string" ? JSON.parse(value) : value,
				empty: false,
			});
		}
	}

	getColumnsSorted = () => sortArrayByProperty(this.props.schema.columns, "order");

	getRowsSorted = () => sortArrayByProperty(this.props.schema.rows, "order");

	getCell = (rowIndex, colIndex) => {
		/*
		 * проверка индексов?
		 */
		const row = this.getRowsSorted()[rowIndex];
		return row?.data?.[colIndex];
	};

	helpClick = () => {
		this.setState(({ showHelp }) => ({ showHelp: !showHelp }));
	};

	updateParentCells = (dataField, rowDataType, rowParentId, depth, doneHandler) => {
		/*
		 * возможно сделать json с описанием компонента
		 * так, чтобы строки ссылались друг на друга
		 * в качестве родителя. ниже - защита от
		 * бесконечной рекурсии. константа 50 - с потолка
		 */
		if (depth >= 50) return;
		const {
			schema: { rows, columns },
		} = this.props;
		/*
		 * нужно найти индекс столбца, с которым
		 * выполняются дальнейшие действия
		 */
		let column_index = 0;
		for (let c = 0; c < columns.length; c++) {
			if (columns[c].name === dataField) {
				column_index = c;
				break;
			}
		}
		let haldled = false;
		// если сейчас изменили числовое значение (или сумму)
		if (rowDataType === "number" || rowDataType === "children_sum") {
			// если задана родительская строка, её нужно обновить
			const parent_row = this.findRowById(rowParentId);
			if (parent_row) {
				// если родитель является суммой дочерних
				if (parent_row.data[column_index].type === "children_sum") {
					let new_parent_val = 0;
					// перебираем дочерние с таким же родителем
					for (let r = 0; r < rows.length; r++) {
						let child_val = this.state.values[r][dataField];
						if (
							this.state.values[r][`${dataField}_type`] === "number" ||
							this.state.values[r][`${dataField}_type`] === "positive_number"
						) {
							child_val = numberCommaToDot(child_val);
						}
						if (
							child_val &&
							!Number.isNaN(parseFloat(child_val)) &&
							(this.state.values[r][`${dataField}_type`] === "number" ||
								this.state.values[r][`${dataField}_type`] === "positive_number" ||
								this.state.values[r][`${dataField}_type`] === "children_sum") &&
							this.state.values[r].parent === rowParentId
						) {
							/*
							 * если значение есть, если это число
							 * и если родительский id совпадает,
							 * то добавляем к сумме
							 */
							new_parent_val += parseFloat(child_val);
						}
					}

					// TODO: Use this.setState( cb() ) - переделать функцию, т.к мы зависим от предыдущего состояния
					// теперь обновляем состояние
					// eslint-disable-next-line react/no-access-state-in-setstate
					const values = this.state.values.map((value) => {
						let rawValue = 0;
						if (value.internal_id === parent_row.internal_id) {
							rawValue = new_parent_val;
						} else if (value[`${dataField}_type`] === "number" || value[`${dataField}_type`] === "positive_number") {
							rawValue = value[dataField] ? parseFloatCommaDot(value[dataField]) : null;
						} else {
							rawValue = value[dataField];
						}
						if (value[`${dataField}_type`] === "number" || value[`${dataField}_type`] === "positive_number") {
							if (rawValue !== null) {
								rawValue = Math.round(rawValue * 100.0) / 100.0;
							}
							value[dataField] = numberDotToComma(rawValue !== null ? rawValue.toString() : null);
						} else {
							value[dataField] = rawValue;
						}
						return value;
					});
					haldled = true;
					this.setState(
						{
							values,
							empty: false,
						},
						() => {
							/*
							 * изменилось значение значение parent_row
							 * если у него есть parent, то его
							 * содержимое тоже нужно обновить
							 */
							if (parent_row.parent) {
								this.updateParentCells(
									dataField,
									parent_row.data[column_index].type,
									parent_row.parent,
									depth + 1,
									doneHandler
								);
							} else {
								doneHandler();
							}
						}
					);
				}
			}
		}
		if (!haldled && depth === 0) {
			doneHandler();
		}
	};

	afterSaveCell = (oldValue, newValue, row, column) => {
		const { dispatcher, schema } = this.props;

		this.updateParentCells(column.dataField, row[`${column.dataField}_type`], row.parent, 0, () => {
			dispatcher.onBlur(schema.id, this.state.values, true, undefined);
		});
	};

	findRowById = (row_id) => {
		if (!row_id) return null;
		const {
			schema: { rows },
		} = this.props;
		return rows?.find((_row) => _row.internal_id === row_id) || null;
	};

	findKeyField = () => {
		const {
			schema: { columns },
		} = this.props;
		return columns?.find((_col) => _col.isKey)?.name;
	};

	isCellEditable = (rowIndex, colIndex) => {
		/*
		 * сначала проверка по столбцу.
		 * если столбец типа 'label'
		 * то редактировать точно нельзя
		 */
		if (this.getColumnsSorted()[colIndex].type === "label") {
			return false;
		}
		/*
		 * на уровне столбца редактирование разрешено.
		 * оно может быть запрещено на уровне строки
		 * label - редактировать нельзя, метка
		 * children_sum - редактировать нельзя, вычисляемое поле
		 */
		const cell = this.getCell(rowIndex, colIndex);
		const cell_type = cell?.type;
		if (cell_type === "label" || cell_type === "children_sum" || cell?.disabled) {
			return false;
		}

		return true;
	};

	// eslint-disable-next-line class-methods-use-this
	validateCell = (newValue, row, column) => {
		if (!newValue) return true;
		const valType = row[`${column.dataField}_type`];

		if (valType === "number") {
			if (!Number.isNaN(parseFloat(strCommaToDot(newValue)))) return true;
			return {
				valid: false,
				message: "Введите число",
			};
		}
		if (valType === "positive_number") {
			if (Math.sign(newValue) === 1) return true;
			return {
				valid: false,
				message: "Введите положительное число",
			};
		}
		if (valType === "text") {
			if (newValue.length === 0) {
				return {
					valid: false,
					message: "Введите строку",
				};
			}
			return true;
		}
		if (valType === "select") {
			if (newValue.length === 0) {
				return {
					valid: false,
					message: "Выберите значение",
				};
			}
			return true;
		}
		return {
			valid: false,
			message: "Не добавлена валидация",
		};
	};

	// eslint-disable-next-line class-methods-use-this
	getDominatingType = (colType, rowType) => (colType === "label" || !rowType ? colType : rowType);

	render() {
		const {
			schema: { disabled, visible, columns },
			error,
		} = this.props;
		const errorMessage = error && error.message;

		// console.log(this.props);

		return isUndefined(visible) || visible ? (
			<Field {...this.props.schema} status={this.state.empty ? "default" : "success"}>
				{
					<BootstrapTable
						keyField={this.findKeyField()}
						cellEdit={cellEditFactory({
							mode: "click",
							blurToSave: true,
							afterSaveCell: this.afterSaveCell,
						})}
						rowClasses={(row, rowIndex) => {
							for (let columnIndex = 0; columnIndex < columns.length; columnIndex++) {
								if (this.isCellEditable(rowIndex, columnIndex)) {
									return "";
								}
							}
							return "row-disabled";
						}}
						bodyClasses="react-bs-container-body_fixed_table"
						data={this.state?.values || []}
						columns={this.getColumnsSorted().map((column: FixedTableColumn) => {
							const result = {
								hidden: false,
								isKey: column.isKey,
								dataField: column.name,
								editable: (cell, row, rowIndex, colIndex) => {
									if (disabled) return false;
									return this.isCellEditable(rowIndex, colIndex);
								},
								validator: (newValue, row, _column) => this.validateCell(newValue, row, _column),
								formatter: emptyChecker,
								formatExtraData: {
									placeholder: column.placeholder ? this.tr(column.placeholder) : "",
									dataField: column.name,
									columnType: column.type,
									defaultOptionsIfHave: column.options,
								},
								text: column.title && column.title.ru ? column.title.ru : column.type,
								editorRenderer: (() => {
									if (column.name === "pos_value") {
										return (editorProps, value) => <InputNumber {...editorProps} value={value} />;
									}

									return null;
								})(),
							};

							if (column.type === "select") {
								// @ts-ignore
								result.editor = {
									type: "select",
									getOptions: (setOptions, data) => {
										const columnName = data.column.dataField;
										const cellOption = data.row?.[`${columnName}_options`];

										if (cellOption) {
											return cellOption;
										}

										return column.options;
									},
								};
							}

							return result;
						})}
					/>
				}
				{/*// @ts-ignore*/}
				<ButtonGroup>
					{this.props?.schema?.description && (
						// @ts-ignore
						<Button onClick={this.helpClick} bsStyle="primary" title="Справка">
							<span className="glyphicon glyphicon-question-sign" />
						</Button>
					)}
				</ButtonGroup>

				<ErrorAlert errorMessage={errorMessage} />
				{this.state.showHelp && (// @ts-ignore
					<Alert bsStyle="warning">
						<div
							dangerouslySetInnerHTML={{
								__html: this.props.schema?.description && this.tr(this.props.schema.description),
							}}
						/>
					</Alert>
				)}
			</Field>
		) : (
			<div />
		);
	}
}

export function createFixedTable(
	schema: IControlSchema,
	api: any,
	dispatcher: IDispatcher = null,
	error: IControlError | undefined,
	visited: boolean,
	allSections: unknown
): React.ReactElement<any> {
	return (
		<FixedTable
			schema={schema as FixedTableSchema}
			allSections={allSections}
			api={api}
			error={error}
			visited={visited}
			dispatcher={dispatcher}
		/>
	);
}
